import React, { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ClipboardList, Eye, Calendar } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { API_BASE_URL } from "../../config";
import { fmtCurrency } from "../../utils/format";

const HEADERS = [
  { id: "saleDate", label: "Date of Sale", align: "left" },
  { id: "buyerLogin", label: "Login", align: "left" },
  { id: "orderId", label: "Order ID", align: "left" },
  { id: "itemsSold", label: "Items Sold (qty)", align: "right", numeric: true },
  { id: "totalSalesValue", label: "Total Sales Value", align: "right", numeric: true },
  { id: "purchaseValue", label: "Purchase Value of Goods", align: "right", numeric: true },
  { id: "commissionTotal", label: "Total Commission", align: "right", numeric: true },
  { id: "packagingCost", label: "Packaging Cost", align: "right", numeric: true },
  { id: "deliveryCost", label: "Total Delivery Costs", align: "right", numeric: true },
  { id: "profit", label: "Profit + Margin", align: "right", numeric: true },
];

const HEADER_SPACER_WIDTH = 14;

const { HEADER_CLASS_MAP, BODY_CELL_CLASS_MAP, COLUMN_STYLE_MAP } = HEADERS.reduce(
  (acc, header) => {
    const alignClass = header.align === "right" ? "text-right" : "text-left";
    const headerClass = `px-3 py-2 font-semibold align-middle ${alignClass}`;
    const bodyClass = `px-3 py-2 align-middle ${alignClass}${header.numeric ? " tabular-nums" : ""}`.trim();
    acc.HEADER_CLASS_MAP[header.id] = headerClass;
    acc.BODY_CELL_CLASS_MAP[header.id] = bodyClass;
    acc.COLUMN_STYLE_MAP[header.id] = header.minWidth ? { minWidth: header.minWidth } : undefined;
    return acc;
  },
  { HEADER_CLASS_MAP: {}, BODY_CELL_CLASS_MAP: {}, COLUMN_STYLE_MAP: {} },
);

const TableColGroup = ({ includeSpacer = false }) => (
  <colgroup>
    {HEADERS.map((header) => (
      <col key={header.id} style={COLUMN_STYLE_MAP[header.id]} />
    ))}
    {includeSpacer && <col style={{ width: `${HEADER_SPACER_WIDTH}px` }} />}
  </colgroup>
);

const TableHeader = () => (
  <thead className="bg-slate-100 text-slate-800 uppercase text-[0.6875rem] tracking-wide border-b border-[#E8E8E8] font-semibold">
    <tr>
      {HEADERS.map((header) => {
        const contentAlignClass = header.align === "right" ? "items-end text-right" : "items-start";
        return (
          <th key={header.id} scope="col" style={COLUMN_STYLE_MAP[header.id]} className={HEADER_CLASS_MAP[header.id]}>
            {header.id === "orderId" ? (
              <div className="flex items-center gap-1.5">
                <span className="text-[0.6875rem] font-semibold">{header.label}</span>
              </div>
            ) : (
              <div className={`flex flex-col ${contentAlignClass} gap-0.5`}>
                <span className="text-[0.6875rem] font-semibold">{header.label}</span>
                {header.subtitle && (
                  <span className="text-[0.6rem] font-normal uppercase tracking-wide text-slate-400">{header.subtitle}</span>
                )}
              </div>
            )}
          </th>
        );
      })}
      <th scope="col" aria-hidden="true" className="p-0" style={{ width: `${HEADER_SPACER_WIDTH}px` }} />
    </tr>
  </thead>
);

const DATE_RANGE_DAYS = 30;
const DATE_RANGE_PRESETS = {
  today: "Today",
  last7: "Last 7 days",
  last30: "Last 30 days",
  thisMonth: "This month",
  custom: "Custom range",
};

const formatDateValue = (value, options) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("pl-PL", options);
};

const isNullish = (value) => value === null || value === undefined;

const formatCurrency = (value, currency = "PLN") => {
  if (isNullish(value)) return "—";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "—";
  return fmtCurrency(numeric, "pl-PL", currency);
};

const getPurchaseValueForMode = (row, salesMode) => {
  if (!row) return null;
  if (salesMode === "gross") {
    return row.purchaseValueGross ?? row.purchaseValue ?? row.purchaseValueNet ?? null;
  }
  return row.purchaseValueNet ?? row.purchaseValue ?? row.purchaseValueGross ?? null;
};

const formatPercent = (value) => {
  if (isNullish(value)) return "—";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "—";
  return `${numeric.toFixed(2)}%`;
};

const formatSignedCurrencyValue = (value, currency = "PLN") => {
  if (isNullish(value)) return "—";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "—";
  const sign = numeric > 0 ? "+" : numeric < 0 ? "-" : "";
  const absolute = fmtCurrency(Math.abs(numeric), "pl-PL", currency);
  return `${sign}${absolute}`;
};

const formatSignedPercentValue = (value) => {
  if (isNullish(value)) return "—";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "—";
  const sign = numeric > 0 ? "+" : numeric < 0 ? "-" : "";
  return `${sign}${Math.abs(numeric).toFixed(2)}%`;
};

const toNumeric = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  if (typeof value === "string" && value.trim() === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const sumNumericValues = (...values) => {
  let total = 0;
  let hasValue = false;
  values.forEach((value) => {
    const numeric = toNumeric(value);
    if (numeric !== null) {
      total += numeric;
      hasValue = true;
    }
  });
  return hasValue ? total : null;
};

const toDateInputValue = (date) => date.toISOString().split("T")[0];

const buildDateRangeFromPreset = (preset) => {
  const today = new Date();
  const normalizedToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  const rangeByPreset = {
    today: () => {
      const value = toDateInputValue(normalizedToday);
      return { dateFrom: value, dateTo: value };
    },
    last7: () => {
      const from = new Date(normalizedToday);
      from.setDate(from.getDate() - 6);
      return { dateFrom: toDateInputValue(from), dateTo: toDateInputValue(normalizedToday) };
    },
    last30: () => {
      const from = new Date(normalizedToday);
      from.setDate(from.getDate() - 29);
      return { dateFrom: toDateInputValue(from), dateTo: toDateInputValue(normalizedToday) };
    },
    thisMonth: () => {
      const from = new Date(normalizedToday.getFullYear(), normalizedToday.getMonth(), 1);
      return { dateFrom: toDateInputValue(from), dateTo: toDateInputValue(normalizedToday) };
    },
  };

  const factory = rangeByPreset[preset];
  return typeof factory === "function" ? factory() : {};
};

const ToggleGroup = ({ label, value, options, onChange }) => (
  <div className="flex flex-col gap-0.5 min-w-[7rem]">
    <span className="text-[0.65rem] uppercase tracking-wide text-slate-500">{label}</span>
    <div className="inline-flex overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
      {options.map((option) => {
        const isActive = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className={`px-4 py-0.5 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 ${
              isActive ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-50"
            }`}
            aria-pressed={isActive}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  </div>
);

const OrderItemCard = ({
  item,
  currency,
  salesMode,
  deliverySummary,
  profitEntries,
  commissionSummary,
  deliveryMethodDisplay,
  smartDisplay,
  onOfferPreview,
}) => {
  const itemCurrency = item.currency || currency;
  const isGrossMode = salesMode === "gross";
  const modeLabel = isGrossMode ? "Gross" : "Net";
  const priceKeySuffix = isGrossMode ? "Gross" : "Net";
  const detailImageSrc = typeof item.imageUrl === "string" && item.imageUrl.trim() ? item.imageUrl : null;
  const offerId = item.allegroOfferId || "—";
  const priceEntries = [
    { label: "Qty", value: item.quantity ?? "—" },
    { label: `Unit ${modeLabel}`, value: formatCurrency(item[`unitPrice${priceKeySuffix}`], itemCurrency) },
    { label: `Total ${modeLabel}`, value: formatCurrency(item[`totalPrice${priceKeySuffix}`], itemCurrency) },
  ];
  const deliveryCostEntries = [
    { label: deliverySummary.costLabel, value: deliverySummary.costValue },
    { label: deliverySummary.additionalLabel, value: deliverySummary.additionalValue },
    { label: deliverySummary.totalLabel, value: deliverySummary.totalValue },
  ];
  const deliveryMetaEntries = [
    { label: "Delivery", value: deliveryMethodDisplay },
    { label: "Smart", value: smartDisplay },
  ];
  const commissionEntries = [
    { label: commissionSummary.commissionLabel, value: commissionSummary.commissionValue },
    { label: commissionSummary.additionalLabel, value: commissionSummary.additionalValue },
    { label: commissionSummary.totalLabel, value: commissionSummary.totalValue },
  ];
  const infoGroups = [
    { key: "price", entries: priceEntries },
    { key: "commission", entries: commissionEntries },
    { key: "deliveryCosts", entries: deliveryCostEntries },
    { key: "deliveryMeta", entries: deliveryMetaEntries },
    { key: "profit", entries: profitEntries, alignRight: true, alignBottom: true },
  ];
  return (
    <div className="flex flex-wrap items-center gap-0.5 rounded border border-slate-200 bg-white p-1 text-[0.6rem] shadow-sm">
      <div className="flex flex-col items-center gap-0.5">
        <button
          type="button"
          onClick={() => onOfferPreview?.(offerId)}
          className="focus:outline-none"
          aria-label="Preview offer"
        >
          {detailImageSrc ? (
            <img
              src={detailImageSrc}
              alt={item.name || offerId || "Order item"}
              className="h-12 w-12 rounded border border-slate-200 object-cover"
              loading="lazy"
            />
          ) : (
            <div className="flex h-12 w-12 items-center justify-center rounded border border-dashed border-slate-300 text-[0.5rem] uppercase tracking-wide text-slate-400">
              No photo
            </div>
          )}
        </button>
        <button
          type="button"
          onClick={() => onOfferPreview?.(offerId)}
          className="text-[0.55rem] font-semibold text-emerald-700 underline decoration-dotted hover:text-emerald-800"
        >
          {offerId}
        </button>
      </div>
      <div className="flex flex-1 flex-wrap gap-0.5">
        {infoGroups.map((group) => (
          <div
            key={`info-group-${group.key}`}
            className={`flex min-w-[5.5rem] flex-col gap-0.25 rounded border border-slate-200 px-1 py-0.5 ${
              group.alignRight ? "ml-auto" : ""
            } ${group.alignBottom ? "self-end mt-auto" : ""}`}
          >
            {group.entries.map((entry) => {
              const valueClassName = entry.valueClassName || "text-[0.6rem] font-semibold text-slate-900";
              const entryKey = entry.key || `${entry.label}-${entry.value}`;
              const isProfitSummary = entry.type === "profitSummary";
              const labelContent = isProfitSummary ? (
                <span className="flex flex-col text-[0.45rem] uppercase tracking-wide text-slate-500 leading-tight">
                  <span>{entry.label}</span>
                  <span className="text-slate-400">{entry.marginLabel || "+ Margin"}</span>
                </span>
              ) : (
                <span className="text-[0.45rem] uppercase tracking-wide text-slate-500">{entry.label}</span>
              );
              return (
                <div key={entryKey} className="flex items-center justify-between gap-0.5">
                  {labelContent}
                  {isProfitSummary ? (
                    <span className="flex flex-col items-end text-right leading-tight">
                      <span className={`text-[0.6rem] font-semibold ${entry.profitToneClass}`}>{entry.profitValue}</span>
                      <span className={`text-[0.55rem] font-semibold ${entry.marginToneClass}`}>{entry.marginValue}</span>
                    </span>
                  ) : (
                    <span className={`${valueClassName} truncate`}>{entry.value}</span>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

const createDefaultDateRange = () => {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - DATE_RANGE_DAYS);
  return { dateFrom: toDateInputValue(from), dateTo: toDateInputValue(to) };
};

const FinancialDetailsRow = ({ order, currency, colSpan, isSelected, salesMode, deliveryMode, onOfferPreview }) => {
  const containerRef = useRef(null);
  const [maxHeight, setMaxHeight] = useState(0);

  useEffect(() => {
    if (!containerRef.current) return;
    if (isSelected) {
      setMaxHeight(containerRef.current.scrollHeight);
    } else {
      setMaxHeight(0);
    }
  }, [isSelected, order.orderId, currency]);

  useEffect(() => {
    if (!isSelected) return;
    if (!containerRef.current) return;
    if (typeof ResizeObserver === "undefined") return;
    const element = containerRef.current;
    const observer = new ResizeObserver(() => {
      setMaxHeight(element.scrollHeight);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [isSelected]);

  const orderItems = Array.isArray(order.orderItems) ? order.orderItems : [];
  const fallbackItems = orderItems.length
    ? orderItems
    : [
        {
          id: order.orderId || order.id,
          name: order.itemName,
          quantity: order.itemsSold,
          allegroOfferId: order.allegroOfferId,
          allegroLineItemId: order.allegroLineItemId,
          unitPriceGross: order.salesValueGross,
          unitPriceNet: order.salesValueNet,
          totalPriceGross: order.salesValueGross,
          totalPriceNet: order.salesValueNet,
          currency,
          imageUrl: order.imageUrl,
        },
      ];
  const itemsToRender = fallbackItems.filter(Boolean);
  const pickValue = (obj, keys = []) => {
    if (!obj || !Array.isArray(keys)) return null;
    for (const key of keys) {
      if (!key) continue;
      const path = String(key).split(".");
      let value = obj;
      let found = true;
      for (const segment of path) {
        if (value && typeof value === "object" && segment in value) {
          value = value[segment];
        } else {
          found = false;
          break;
        }
      }
      if (!found) continue;
      if (!isNullish(value) && value !== "") {
        return value;
      }
    }
    return null;
  };
  const deliveryMethodValue = pickValue(order, [
    "deliveryMethodName",
    "deliveryMethodLabel",
    "deliveryMethod",
    "deliveryMeta.methodName",
    "deliveryMeta.method.name",
    "delivery.methodName",
    "delivery.method",
  ]);
  const deliveryMethodDisplay = deliveryMethodValue || "—";
  const smartRaw = pickValue(order, ["deliverySmart", "smart", "isSmart"]);
  const smartDisplay = (() => {
    if (typeof smartRaw === "boolean") {
      return smartRaw ? "Yes" : "No";
    }
    if (smartRaw === 1 || smartRaw === "1") {
      return "Yes";
    }
    if (smartRaw === 0 || smartRaw === "0") {
      return "No";
    }
    return smartRaw || "—";
  })();
  const isGrossMode = salesMode === "gross";
  const salesModeLabel = isGrossMode ? "Gross" : "Net";
  const pickSalesModeValue = (spec) => pickValue(order, isGrossMode ? spec.grossKeys : spec.netKeys);
  const commissionValueRaw = pickSalesModeValue({
    netKeys: ["commissionCostNet", "commissionTotal"],
    grossKeys: ["commissionCostGross", "commissionTotal"],
  });
  const commissionValue = formatCurrency(commissionValueRaw, currency);
  const commissionAdditionalValueRaw = pickSalesModeValue({
    netKeys: ["commissionCostAdditionalNet", "commissionCostAdditional"],
    grossKeys: ["commissionCostAdditionalGross", "commissionCostAdditional"],
  });
  const commissionAdditionalValue = formatCurrency(commissionAdditionalValueRaw, currency);
  const commissionTotalValue = formatCurrency(sumNumericValues(commissionValueRaw, commissionAdditionalValueRaw), currency);
  const deliveryModeLabel = deliveryMode === "theoretical" ? "Theoretical" : "Actual";
  const deliveryCostRaw = pickValue(
    order,
    deliveryMode === "theoretical"
      ? ["deliveryCostTheoretical", "deliveryCostActual", "deliveryCost"]
      : ["deliveryCostActual", "deliveryCostTheoretical", "deliveryCost"],
  );
  const deliveryCostValue = formatCurrency(deliveryCostRaw, currency);
  const deliveryAdditionalRaw = pickValue(
    order,
    deliveryMode === "theoretical"
      ? [
          "deliveryCostAdditionalTheoretical",
          "deliveryCostAdditionalActual",
          "deliveryCostAdditionalTheoreticalNet",
          "deliveryCostAdditionalActualNet",
          "deliveryCostAdditional",
        ]
      : [
          "deliveryCostAdditionalActual",
          "deliveryCostAdditionalTheoretical",
          "deliveryCostAdditionalActualNet",
          "deliveryCostAdditionalTheoreticalNet",
          "deliveryCostAdditional",
        ],
  );
  const deliveryAdditionalValue = formatCurrency(deliveryAdditionalRaw, currency);
  const deliveryTotalValue = formatCurrency(sumNumericValues(deliveryCostRaw, deliveryAdditionalRaw), currency);
  const deliverySummary = {
    costLabel: `Delivery cost (${deliveryModeLabel.toLowerCase()})`,
    costValue: deliveryCostValue,
    additionalLabel: `Additional delivery (${deliveryModeLabel.toLowerCase()})`,
    additionalValue: deliveryAdditionalValue,
    totalLabel: `Total delivery (${deliveryModeLabel.toLowerCase()})`,
    totalValue: deliveryTotalValue,
  };
  const commissionSummary = {
    commissionLabel: `Commission (${salesModeLabel})`,
    commissionValue,
    additionalLabel: `Additional commission (${salesModeLabel})`,
    additionalValue: commissionAdditionalValue,
    totalLabel: `Total commission (${salesModeLabel})`,
    totalValue: commissionTotalValue,
  };
  const profitValueKeys =
    deliveryMode === "theoretical"
      ? ["profitValueTheoretical", "profitValueActual", "profitValue"]
      : ["profitValueActual", "profitValue"];
  const profitMarginKeys =
    deliveryMode === "theoretical"
      ? ["profitMarginTheoretical", "profitMarginActual", "profitMargin"]
      : ["profitMarginActual", "profitMargin"];
  const resolveItemMetric = (item, keys) => {
    const itemValue = pickValue(item, keys);
    if (!isNullish(itemValue)) {
      return itemValue;
    }
    return pickValue(order, keys);
  };
  const buildProfitEntries = (item) => {
    const itemCurrency = item.currency || currency;
    const profitValue = resolveItemMetric(item, profitValueKeys);
    const marginValue = resolveItemMetric(item, profitMarginKeys);
    const profitNumeric = toNumeric(profitValue);
    const marginNumeric = toNumeric(marginValue);
    const quantityNumeric = toNumeric(item.quantity);
    const profitToneClass = Number.isFinite(profitNumeric)
      ? profitNumeric > 0
        ? "text-emerald-700"
        : profitNumeric < 0
          ? "text-rose-700"
          : "text-slate-700"
      : "text-slate-500";
    const marginToneClass = Number.isFinite(marginNumeric) ? "text-slate-500" : "text-slate-400";
    const entryKeyBase = item.id || item.allegroLineItemId || item.allegroOfferId || item.name || order.orderId || "entry";
    const entries = [
      {
        type: "profitSummary",
        key: `profit-summary-${entryKeyBase}`,
        label: `Profit (${deliveryModeLabel})`,
        profitValue: formatSignedCurrencyValue(profitValue, itemCurrency),
        profitToneClass,
        marginValue: formatSignedPercentValue(marginValue),
        marginToneClass,
        marginLabel: "+ Margin",
      },
    ];
    if (Number.isFinite(profitNumeric) && Number.isFinite(quantityNumeric) && quantityNumeric > 0) {
      const profitPerItem = profitNumeric / quantityNumeric;
      entries.push({
        type: "profitPerItem",
        key: `profit-per-item-${entryKeyBase}`,
        label: "Profit per item",
        value: formatSignedCurrencyValue(profitPerItem, itemCurrency),
        valueClassName: `text-[0.55rem] font-semibold ${profitToneClass}`,
      });
    }
    return entries;
  };

  return (
    <tr className={`transition-colors ${isSelected ? "border-t border-slate-200" : "border-t border-transparent"}`}>
      <td colSpan={colSpan} className="p-0">
        <div
          style={{
            maxHeight: isSelected ? `${maxHeight}px` : "0px",
            opacity: isSelected ? 1 : 0,
          }}
          className="overflow-hidden transition-all duration-300 ease-in-out"
          aria-hidden={!isSelected}
        >
          <div ref={containerRef} className="px-3 py-2 text-sm text-slate-700 space-y-2">
            {itemsToRender.length > 0 && (
              <div className="space-y-1">
                {itemsToRender.map((item) => (
                  <OrderItemCard
                    key={item.id || item.allegroLineItemId || `${item.name}-${item.quantity}`}
                    item={item}
                    currency={currency}
                    salesMode={salesMode}
                    deliverySummary={deliverySummary}
                    profitEntries={buildProfitEntries(item)}
                    commissionSummary={commissionSummary}
                    deliveryMethodDisplay={deliveryMethodDisplay}
                    smartDisplay={smartDisplay}
                    onOfferPreview={onOfferPreview}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
};

export const OrdersDev = () => {
  const { authFetch, authToken } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [expandedOrderIds, setExpandedOrderIds] = useState(() => new Set());
  const [filters, setFilters] = useState(createDefaultDateRange);
  const [appliedFilters, setAppliedFilters] = useState(createDefaultDateRange);
  const [salesMode, setSalesMode] = useState("net");
  const [deliveryMode, setDeliveryMode] = useState("actual");
  const [previewOfferId, setPreviewOfferId] = useState(null);
  const [dateRangePreset, setDateRangePreset] = useState("last30");
  const [isDateMenuOpen, setIsDateMenuOpen] = useState(false);
  const [dateMenuPosition, setDateMenuPosition] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const dateMenuRef = useRef(null);
  const dateRangeLabel = useMemo(() => {
    if (dateRangePreset === "custom" && filters.dateFrom && filters.dateTo) {
      return `Date range: ${filters.dateFrom} – ${filters.dateTo}`;
    }
    const presetLabel = DATE_RANGE_PRESETS[dateRangePreset] || DATE_RANGE_PRESETS.custom;
    return `Date range: ${presetLabel}`;
  }, [dateRangePreset, filters.dateFrom, filters.dateTo]);

  const expandableOrderIds = useMemo(() => {
    return rows
      .map((row) => (row.orderId === null || row.orderId === undefined ? "" : String(row.orderId)))
      .filter((id) => id.length > 0);
  }, [rows]);
  const loadRows = useCallback(async () => {
    if (!authToken) {
      setRows([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (appliedFilters.dateFrom) params.set("date_from", appliedFilters.dateFrom);
      if (appliedFilters.dateTo) params.set("date_to", appliedFilters.dateTo);
      const query = params.toString();
      const resp = await authFetch(
        `${API_BASE_URL}/api/orders/financial/${query ? `?${query}` : ""}`,
      );
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.detail || data.message || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      setRows(Array.isArray(data?.results) ? data.results : []);
    } catch (e) {
      setError(e.message || "Failed to load financial data.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, authFetch, authToken]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const updateDateMenuPosition = useCallback(() => {
    if (!dateMenuRef.current) return;
    const rect = dateMenuRef.current.getBoundingClientRect();
    setDateMenuPosition({
      top: rect.bottom + window.scrollY + 4,
      left: rect.left + window.scrollX,
      width: rect.width,
    });
  }, []);

  useLayoutEffect(() => {
    if (!isDateMenuOpen) return;
    updateDateMenuPosition();
    const handleScroll = () => updateDateMenuPosition();
    window.addEventListener("resize", updateDateMenuPosition);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      window.removeEventListener("resize", updateDateMenuPosition);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [isDateMenuOpen, updateDateMenuPosition]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dateMenuRef.current && !dateMenuRef.current.contains(event.target)) {
        setIsDateMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!selectedOrderId) return;
    if (!rows.some((row) => row.orderId === selectedOrderId)) {
      setSelectedOrderId(null);
    }
  }, [rows, selectedOrderId]);

  useEffect(() => {
    setExpandedOrderIds((prev) => {
      if (!prev.size) return prev;
      const validIds = new Set(expandableOrderIds);
      let changed = false;
      const next = new Set();
      prev.forEach((value) => {
        if (validIds.has(value)) {
          next.add(value);
        } else {
          changed = true;
        }
      });
      if (!changed && next.size === prev.size) {
        return prev;
      }
      return next;
    });
  }, [expandableOrderIds]);

  const handleRowClick = useCallback((orderId) => {
    setSelectedOrderId((prev) => (prev === orderId ? null : orderId));
  }, []);

  const handlePresetSelect = useCallback((presetKey) => {
    setDateRangePreset(presetKey);
    if (presetKey === "custom") {
      setIsDateMenuOpen(true);
      return;
    }
    const range = buildDateRangeFromPreset(presetKey);
    setFilters(range);
    setAppliedFilters(range);
    setIsDateMenuOpen(false);
  }, []);

  const toggleOrderIdDisplay = useCallback((orderId) => {
    const key = orderId === null || orderId === undefined ? "" : String(orderId);
    setExpandedOrderIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const handleFilterChange = useCallback((field) => (event) => {
    const { value } = event.target;
    setFilters((prev) => ({ ...prev, [field]: value }));
  }, []);
  const handleSearchChange = useCallback((event) => {
    setSearchQuery(event.target.value);
  }, []);

  const handleApplyFilters = useCallback(() => {
    setAppliedFilters({ ...filters });
    setIsDateMenuOpen(false);
  }, [filters]);

  const handleResetFilters = useCallback(() => {
    const defaults = createDefaultDateRange();
    setFilters(defaults);
    setAppliedFilters(defaults);
    setDateRangePreset("last30");
    setIsDateMenuOpen(false);
  }, []);

const tableBody = useMemo(() => {
  if (!authToken) {
    return (
      <tr className="border-t border-slate-200">
          <td colSpan={HEADERS.length} className="px-2 py-3 text-center text-slate-500">
            Sign in to view orders.
          </td>
        </tr>
      );
    }
    if (loading) {
      return (
        <tr className="border-t border-slate-200">
          <td colSpan={HEADERS.length} className="px-2 py-3 text-center text-slate-500">
            Loading orders…
          </td>
        </tr>
      );
    }
    if (error) {
      return (
        <tr className="border-t border-slate-200">
          <td colSpan={HEADERS.length} className="px-2 py-3 text-center text-red-500">
            {error}
          </td>
        </tr>
      );
    }
    if (!rows.length) {
      return (
        <tr className="border-t border-slate-200">
          <td colSpan={HEADERS.length} className="px-2 py-3 text-center text-slate-400">
            No data for the selected range.
          </td>
        </tr>
      );
    }

    const query = searchQuery.trim().toLowerCase();
    const filteredRows = query
      ? rows.filter((row) => {
          const currency = row.currency || "PLN";
          const totalSalesValue =
            salesMode === "gross"
              ? row.totalSalesValueGross ?? row.totalSalesValueNet ?? row.totalSalesValue
              : row.totalSalesValueNet ?? row.totalSalesValue;
          const deliveryValue =
            deliveryMode === "theoretical"
              ? row.deliveryCostTheoretical ?? row.deliveryCostActual ?? row.deliveryCost
              : row.deliveryCostActual ?? row.deliveryCost;
          const profitValue =
            deliveryMode === "theoretical"
              ? row.profitValueTheoretical ?? row.profitValueActual ?? row.profitValue
              : row.profitValueActual ?? row.profitValue;
          const marginValue =
            deliveryMode === "theoretical"
              ? row.profitMarginTheoretical ?? row.profitMarginActual ?? row.profitMargin
              : row.profitMarginActual ?? row.profitMargin;
          const totalSalesNumeric = toNumeric(totalSalesValue);
          const purchaseValueForMode = getPurchaseValueForMode(row, salesMode);
          const purchaseNumeric = toNumeric(purchaseValueForMode);
          const commissionNumeric = toNumeric(row.commissionTotal);
          const packagingNumeric = toNumeric(row.packagingCost);
          const deliveryNumeric = toNumeric(deliveryValue);
          let profitNumeric = toNumeric(profitValue);
          let marginNumeric = toNumeric(marginValue);
          const computedProfit =
            Number.isFinite(totalSalesNumeric) &&
            Number.isFinite(purchaseNumeric) &&
            Number.isFinite(commissionNumeric) &&
            Number.isFinite(packagingNumeric) &&
            Number.isFinite(deliveryNumeric)
              ? totalSalesNumeric - purchaseNumeric - commissionNumeric - packagingNumeric - deliveryNumeric
              : null;
          if (!Number.isFinite(profitNumeric) && Number.isFinite(computedProfit)) {
            profitNumeric = computedProfit;
            profitValue = computedProfit;
          }
          if (!Number.isFinite(marginNumeric) && Number.isFinite(profitNumeric) && Number.isFinite(totalSalesNumeric) && totalSalesNumeric !== 0) {
            marginNumeric = (profitNumeric / totalSalesNumeric) * 100;
            marginValue = marginNumeric;
          }

          const haystack = [
            formatDateValue(row.saleDate, { dateStyle: "medium" }),
            row.buyerLogin,
            row.orderId,
            row.itemsSold,
            formatCurrency(totalSalesValue, currency),
            formatCurrency(purchaseValueForMode, currency),
            formatCurrency(row.commissionTotal, currency),
            formatCurrency(row.packagingCost, currency),
            formatCurrency(deliveryValue, currency),
            formatSignedCurrencyValue(profitValue, currency),
            formatSignedPercentValue(marginValue),
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return haystack.includes(query);
        })
      : rows;

    if (!filteredRows.length) {
      return (
        <tr className="border-t border-slate-200">
          <td colSpan={HEADERS.length} className="px-2 py-3 text-center text-slate-400">
            {rows.length ? "No matches for your search." : "No data for the selected range."}
          </td>
        </tr>
      );
    }

    return filteredRows.map((row, index) => {
      const currency = row.currency || "PLN";
      const isSelected = selectedOrderId === row.orderId;
      const orderIdString = row.orderId === null || row.orderId === undefined ? "" : String(row.orderId);
      const isOrderIdExpanded = expandedOrderIds.has(orderIdString);
      const hasOrderId = Boolean(orderIdString);
      const rowStripeClass = !isSelected && index % 2 === 1 ? "bg-slate-50/60" : "";
      const totalSalesValue =
        salesMode === "gross"
          ? row.totalSalesValueGross ?? row.totalSalesValueNet ?? row.totalSalesValue
          : row.totalSalesValueNet ?? row.totalSalesValue;
      const totalSalesDisplay = formatCurrency(totalSalesValue, currency);
      const totalSalesNumeric = toNumeric(totalSalesValue);
      const purchaseValueForMode = getPurchaseValueForMode(row, salesMode);
      const purchaseDisplay = formatCurrency(purchaseValueForMode, currency);
      const purchaseNumeric = toNumeric(purchaseValueForMode);
      const commissionDisplay = formatCurrency(row.commissionTotal, currency);
      const commissionNumeric = toNumeric(row.commissionTotal);
      const packagingDisplay = formatCurrency(row.packagingCost, currency);
      const packagingNumeric = toNumeric(row.packagingCost);
      const deliveryValue =
        deliveryMode === "theoretical"
          ? row.deliveryCostTheoretical ?? row.deliveryCostActual ?? row.deliveryCost
          : row.deliveryCostActual ?? row.deliveryCost;
      const deliveryDisplay = formatCurrency(deliveryValue, currency);
      const deliveryNumeric = toNumeric(deliveryValue);
      const profitValue =
        deliveryMode === "theoretical"
          ? row.profitValueTheoretical ?? row.profitValueActual ?? row.profitValue
          : row.profitValueActual ?? row.profitValue;
      const marginValue =
        deliveryMode === "theoretical"
          ? row.profitMarginTheoretical ?? row.profitMarginActual ?? row.profitMargin
          : row.profitMarginActual ?? row.profitMargin;
      let profitNumeric = toNumeric(profitValue);
      let marginNumeric = toNumeric(marginValue);
      const computedProfit =
        Number.isFinite(totalSalesNumeric) &&
        Number.isFinite(purchaseNumeric) &&
        Number.isFinite(commissionNumeric) &&
        Number.isFinite(packagingNumeric) &&
        Number.isFinite(deliveryNumeric)
          ? totalSalesNumeric - purchaseNumeric - commissionNumeric - packagingNumeric - deliveryNumeric
          : null;
      if (!Number.isFinite(profitNumeric) && Number.isFinite(computedProfit)) {
        profitNumeric = computedProfit;
      }
      if (!Number.isFinite(marginNumeric) && Number.isFinite(profitNumeric) && Number.isFinite(totalSalesNumeric) && totalSalesNumeric !== 0) {
        marginNumeric = (profitNumeric / totalSalesNumeric) * 100;
      }
      const profitValueNormalized = Number.isFinite(profitNumeric) ? profitNumeric : profitValue;
      const marginValueNormalized = Number.isFinite(marginNumeric) ? marginNumeric : marginValue;
      const hasProfit = Number.isFinite(profitNumeric);
      const hasMargin = Number.isFinite(marginNumeric);
      const profitSignedDisplay = formatSignedCurrencyValue(profitValueNormalized, currency);
      const marginSignedDisplay = formatSignedPercentValue(marginValueNormalized);
      const profitToneClass = Number.isFinite(profitNumeric)
        ? profitNumeric > 0
          ? "text-emerald-700"
          : profitNumeric < 0
            ? "text-rose-700"
            : "text-slate-700"
        : "text-slate-600";

      return (
        <Fragment key={row.orderId}>
          <tr
            className={`border-t border-slate-200 transition-colors ${
              isSelected ? "bg-emerald-50/60" : `${rowStripeClass} hover:bg-slate-50`
            } cursor-pointer`}
            onClick={() => handleRowClick(row.orderId)}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleRowClick(row.orderId);
              }
            }}
            aria-expanded={isSelected}
          >
            <td
              style={COLUMN_STYLE_MAP.saleDate}
              className={`${BODY_CELL_CLASS_MAP.saleDate} text-slate-700 whitespace-nowrap`}
            >
              {formatDateValue(row.saleDate, { dateStyle: "medium" })}
            </td>
            <td
              style={COLUMN_STYLE_MAP.buyerLogin}
              className={`${BODY_CELL_CLASS_MAP.buyerLogin} text-slate-700 whitespace-nowrap`}
            >
              {row.buyerLogin || "—"}
            </td>
            <td style={COLUMN_STYLE_MAP.orderId} className={`${BODY_CELL_CLASS_MAP.orderId} text-slate-700`}>
              <div className="relative flex h-full items-center justify-center">
                {hasOrderId ? (
                  <>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        toggleOrderIdDisplay(orderIdString);
                      }}
                      className="flex items-center justify-center rounded border border-slate-300 p-1 text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-1"
                      aria-label={isOrderIdExpanded ? "Hide order ID" : "Show order ID"}
                      aria-expanded={isOrderIdExpanded}
                    >
                      <Eye size={16} aria-hidden="true" />
                    </button>
                    {isOrderIdExpanded && (
                      <div className="absolute right-0 top-full z-10 mt-1 min-w-[200px] max-w-[260px] rounded-[6px] border border-slate-200 bg-[#fdfdfd] px-3 py-2 text-sm font-mono leading-[1.4] text-slate-900 shadow-[0_6px_18px_rgba(0,0,0,0.12)]">
                        {orderIdString}
                      </div>
                    )}
                  </>
                ) : (
                  <span className="text-slate-400">—</span>
                )}
              </div>
            </td>
            <td style={COLUMN_STYLE_MAP.itemsSold} className={`${BODY_CELL_CLASS_MAP.itemsSold} text-slate-700`}>
              {row.itemsSold ?? "—"}
            </td>
            <td
              style={COLUMN_STYLE_MAP.totalSalesValue}
              className={`${BODY_CELL_CLASS_MAP.totalSalesValue} text-slate-900 font-semibold`}
            >
              {totalSalesDisplay}
            </td>
            <td style={COLUMN_STYLE_MAP.purchaseValue} className={`${BODY_CELL_CLASS_MAP.purchaseValue} text-slate-700`}>
              {purchaseDisplay}
            </td>
            <td
              style={COLUMN_STYLE_MAP.commissionTotal}
              className={`${BODY_CELL_CLASS_MAP.commissionTotal} text-slate-700`}
            >
              {commissionDisplay}
            </td>
            <td
              style={COLUMN_STYLE_MAP.packagingCost}
              className={`${BODY_CELL_CLASS_MAP.packagingCost} text-slate-700`}
            >
              {packagingDisplay}
            </td>
            <td
              style={COLUMN_STYLE_MAP.deliveryCost}
              className={`${BODY_CELL_CLASS_MAP.deliveryCost} text-slate-700`}
            >
              {deliveryDisplay}
            </td>
            <td style={COLUMN_STYLE_MAP.profit} className={`${BODY_CELL_CLASS_MAP.profit} font-semibold`}>
              <div className="flex justify-end">
                {hasProfit ? (
                  <span className={`flex flex-col items-end text-sm font-semibold leading-snug ${profitToneClass}`}>
                    <span>{profitSignedDisplay}</span>
                    {hasMargin && <span className="text-[0.7rem] font-normal text-slate-500 leading-tight">{marginSignedDisplay}</span>}
                  </span>
                ) : (
                  <span className="text-xs font-semibold text-slate-500">No data</span>
                )}
              </div>
          </td>
          </tr>
          <FinancialDetailsRow
            order={row}
            currency={currency}
            colSpan={HEADERS.length}
            isSelected={isSelected}
            salesMode={salesMode}
            deliveryMode={deliveryMode}
            onOfferPreview={setPreviewOfferId}
          />
        </Fragment>
      );
    });
  }, [
    authToken,
    error,
    expandedOrderIds,
    handleRowClick,
    loading,
    rows,
    salesMode,
    deliveryMode,
    selectedOrderId,
    toggleOrderIdDisplay,
    searchQuery,
  ]);

  return (
    <>
      <div className="flex w-full min-h-0 flex-col gap-2 overflow-x-hidden overflow-y-visible h-[calc(100vh-2rem)] md:h-[calc(100vh-3.1rem)] max-h-[calc(100vh-2rem)] md:max-h-[calc(100vh-3.1rem)]">
        <div className="w-full rounded-lg border border-slate-300 bg-slate-50 px-2 py-1 shadow-sm">
          <div className="w-full overflow-x-auto overflow-y-visible">
            <div className="flex min-w-fit flex-wrap items-center gap-1.5 text-slate-900">
              <div className="flex items-center gap-1.5 pr-2 border-r border-slate-200">
                <ClipboardList size={22} className="text-slate-700" />
                <h2 className="text-lg font-semibold leading-tight">Orders Dev</h2>
              </div>
              <div className="flex min-w-[260px] flex-1 flex-wrap items-center gap-1.5">
                <div className="relative" ref={dateMenuRef}>
                <button
                  type="button"
                  onClick={() => setIsDateMenuOpen((open) => !open)}
                  className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1 text-sm font-semibold text-slate-900 shadow-sm hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-1"
                  aria-expanded={isDateMenuOpen}
                >
                  <Calendar size={16} className="text-slate-500" aria-hidden="true" />
                  <span>{dateRangeLabel}</span>
                </button>
                {isDateMenuOpen && dateMenuPosition && (
                  <div
                    className="fixed z-[100] mt-1 w-64 rounded-lg border border-slate-200 bg-white p-2 shadow-[0_4px_18px_rgba(0,0,0,0.08)]"
                    style={{ top: dateMenuPosition.top, left: dateMenuPosition.left, width: Math.max(256, dateMenuPosition.width) }}
                  >
                    <div className="flex flex-col gap-1">
                      {["today", "last7", "last30", "thisMonth"].map((presetKey) => (
                        <button
                          key={presetKey}
                          type="button"
                          onClick={() => handlePresetSelect(presetKey)}
                          className={`flex w-full items-center justify-between rounded-md px-2 py-1.5 text-sm ${
                            dateRangePreset === presetKey ? "bg-emerald-50 text-emerald-700" : "text-slate-700 hover:bg-slate-50"
                          }`}
                        >
                          <span>{DATE_RANGE_PRESETS[presetKey]}</span>
                          {dateRangePreset === presetKey && <span className="text-[0.7rem] font-semibold">•</span>}
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => handlePresetSelect("custom")}
                        className={`flex w-full items-center justify-between rounded-md px-2 py-1.5 text-sm ${
                          dateRangePreset === "custom" ? "bg-emerald-50 text-emerald-700" : "text-slate-700 hover:bg-slate-50"
                        }`}
                      >
                        <span>{DATE_RANGE_PRESETS.custom}</span>
                        {dateRangePreset === "custom" && <span className="text-[0.7rem] font-semibold">•</span>}
                      </button>
                    </div>
                    {dateRangePreset === "custom" && (
                      <div className="mt-2 space-y-2 rounded-md border border-slate-200 bg-slate-50 p-2">
                        <div className="flex flex-col gap-1">
                          <label htmlFor="custom-date-from" className="text-[0.7rem] font-semibold uppercase tracking-wide text-slate-500">
                            Start date
                          </label>
                          <input
                            id="custom-date-from"
                            type="date"
                            value={filters.dateFrom || ""}
                            onChange={handleFilterChange("dateFrom")}
                            className="rounded-md border border-slate-300 px-2 py-1 text-sm text-slate-700 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label htmlFor="custom-date-to" className="text-[0.7rem] font-semibold uppercase tracking-wide text-slate-500">
                            End date
                          </label>
                          <input
                            id="custom-date-to"
                            type="date"
                            value={filters.dateTo || ""}
                            onChange={handleFilterChange("dateTo")}
                            className="rounded-md border border-slate-300 px-2 py-1 text-sm text-slate-700 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                          />
                        </div>
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={handleApplyFilters}
                            className="rounded-md bg-emerald-600 px-2.5 py-1 text-sm font-semibold text-white shadow-sm hover:bg-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:ring-offset-1"
                          >
                            Apply custom range
                          </button>
                          <button
                            type="button"
                            onClick={() => setIsDateMenuOpen(false)}
                            className="rounded-md border border-slate-300 px-2.5 py-1 text-sm font-semibold text-slate-700 hover:bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:ring-offset-1"
                          >
                            Close
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1.5">
                <label htmlFor="orders-search" className="sr-only">
                  Search orders
                </label>
                <div className="relative">
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 20 20"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    className={`pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 transition-opacity ${
                      searchQuery ? "opacity-0" : "opacity-100"
                    }`}
                  >
                    <circle cx="9" cy="9" r="5" />
                    <path d="m12.5 12.5 3 3" strokeLinecap="round" />
                  </svg>
                  <input
                    id="orders-search"
                    type="text"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    placeholder="Search orders..."
                    className={`w-48 rounded-lg border border-slate-300 bg-white pr-3 py-1 text-sm text-slate-800 shadow-sm placeholder:text-slate-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-1 ${
                      searchQuery ? "pl-3" : "pl-9"
                    }`}
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={handleResetFilters}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:-translate-y-[1px] hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-1"
              >
                <svg
                  aria-hidden="true"
                  viewBox="0 0 20 20"
                  className="h-4 w-4 text-slate-600"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path d="M15 8a6 6 0 10.19 2.19M15 8V4m0 4h-4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Reset
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              <ToggleGroup
                label="Sales value"
                value={salesMode}
                onChange={setSalesMode}
                options={[
                  { label: "Net", value: "net" },
                  { label: "Gross", value: "gross" },
                ]}
              />
              <ToggleGroup
                label="Delivery cost"
                value={deliveryMode}
                onChange={setDeliveryMode}
                options={[
                  { label: "Actual", value: "actual" },
                  { label: "Theoretical", value: "theoretical" },
                ]}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-hidden rounded-lg border border-slate-200 bg-white">
          <div className="flex h-full flex-col overflow-x-auto">
            <table className="w-full min-w-full table-fixed border-collapse text-sm leading-tight">
              <TableColGroup includeSpacer />
              <TableHeader />
            </table>
            <div className="flex-1 min-h-0 overflow-y-auto">
              <table className="w-full min-w-full table-fixed border-collapse text-sm leading-tight">
                <TableColGroup />
                <tbody>{tableBody}</tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      {previewOfferId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-2.5">
          <div className="w-full max-w-sm rounded-xl border border-slate-200 bg-white p-2.5 shadow-lg">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-700">Offer preview</h3>
              <button
                type="button"
                onClick={() => setPreviewOfferId(null)}
                className="text-sm text-slate-500 hover:text-slate-700"
              >
                Close
              </button>
            </div>
            <div className="mt-2 space-y-1.5 text-sm text-slate-600">
              <p>Offer ID: <span className="font-semibold text-slate-900">{previewOfferId}</span></p>
              <p className="text-xs text-slate-500">Integrate your offer details component here.</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OrdersDev;
